"use client";

import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Modal,
  IconButton,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import Banner from "./Banner";
import ZaynCourasel from "./ZaynCourasel";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const HeroSectionZayn = memo(function HeroSectionZayn() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const videoSectionRef = useRef(null);
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setCurrentTime(current);
      setDuration(total);
      setProgress((current / total) * 100);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current && progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
      setProgress(percentage * 100);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSeek(e);
  };
  const handleMouseMove = (e) => {
    if (isDragging) {
      handleSeek(e);
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    if (!isClient) return;
    const handleScroll = () => {
      if (videoSectionRef.current) {
        const rect = videoSectionRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const startTrigger = windowHeight * 0.8;
        const endTrigger = windowHeight * 0.2;
        if (rect.top <= startTrigger && rect.bottom >= endTrigger) {
          const progress = Math.min(
            Math.max(
              (startTrigger - rect.top) / (startTrigger - endTrigger),
              0,
            ),
            1,
          );
          setScrollProgress(progress);
        } else if (rect.top > startTrigger) {
          setScrollProgress(0);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isClient]);

  const isMobile = isClient && window.innerWidth < 768;

  const borderRadiusValue = isClient
    ? scrollProgress === 0
      ? "50%"
      : isMobile
        ? `${Math.max(16, 140 - scrollProgress * 124)}px`
        : `${Math.max(32, 250 - scrollProgress * 218)}px`
    : "50%";

  const videoWidth = isClient
    ? isMobile
      ? scrollProgress === 0
        ? 280
        : 280 + scrollProgress * 320
      : scrollProgress === 0
        ? 380
        : 380 + scrollProgress * 600
    : 380;

  const videoHeight = isClient
    ? isMobile
      ? scrollProgress === 0
        ? 280
        : 280 + scrollProgress * 10
      : scrollProgress === 0
        ? 400
        : 400 + scrollProgress * 150
    : 400;

  return (
    <Box
      suppressHydrationWarning={true}
      aria-label="AI interview platform background with AI recruiting software interface"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: 6,
        pb: 12,
        backgroundImage: `url('${IMAGES.ZAYN_LANDING_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      {/* Banner */}
      <Box sx={{ pt: { xs: "1%", sm: "1%", md: "1%", lg: "5%", xl: "-10%" } }}>
        {/* <Banner /> */}
      </Box>

      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: { xs: isArabic ? "right" : "left", lg: "center" },
            ml: isArabic ? 0 : { xs: "1%", sm: "1%", md: "1%", lg: 0 },
            mr: isArabic ? { xs: "1%", sm: "1%", md: "1%", lg: 0 } : 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Badge */}
          <Box mb={{ xs: 4, sm: 5, md: 3 }} sx={{ mt: { xs: 4, md: "1%" } }}>
            <Chip
              label={t("heroSectionZayn.chip")}
              sx={{
                backgroundColor: "transparent",
                color: "#647482",
                border: "1px solid #DDE4E8",
                mb: { xs: 1.5, md: 0 },
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
                fontWeight: "500",
                textTransform: "capitalize",
                px: { xs: 0.5, sm: 0.7 },
                py: { xs: 1.2, sm: 1.5, md: 2.5 },
                fontFamily: "var(--font-outfit)",
                borderRadius: "50px",
              }}
            />
          </Box>

          {/* Headings */}
          <Typography
            variant="h1"
            sx={{
              fontSize: {
                xs: "32px",
                sm: "42px",
                md: "42px",
                lg: "42px",
                xl: "45px",
              },
              fontWeight: "bold",
              color: "#775CE5",
              mb: 1,
              lineHeight: 1.2,
              mt: { xs: "-6%", md: "1%" },
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("heroSectionZayn.heading1")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: {
                xs: "32px",
                sm: "42px",
                md: "42px",
                lg: "42px",
                xl: "45px",
              },
              fontWeight: "bold",
              color: "#000",
              mb: 0.5,
              lineHeight: 1.2,
              mt: "1%",
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("heroSectionZayn.heading2")}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontSize: {
                xs: "32px",
                sm: "42px",
                md: "42px",
                lg: "42px",
                xl: "45px",
              },
              fontWeight: "bold",
              color: "#000",
              mb: 2,
              lineHeight: 1.2,
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("heroSectionZayn.heading3")}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              fontSize: {
                xs: "14px",
                sm: "18px",
                md: "18px",
                lg: "18px",
                xl: "19px",
              },
              color: "#435268",
              maxWidth: { xs: "90%", sm: "80%", md: "700px" },
              mx: { xs: 0, sm: 0, md: "auto" },
              mb: 4,
              lineHeight: 1.6,
              fontFamily: "var(--font-outfit)",
              textAlign: { xs: isArabic ? "right" : "left", lg: "center" },
            }}
          >
            {t("heroSectionZayn.subtitle")}
          </Typography>

          {/* CTA Button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: {
                xs: isArabic ? "flex-start" : "flex-start",
                md: "center",
                lg: "center",
              },
              gap: 0,
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                const section = document.getElementById("talkToZaynSection");
                if (section) {
                  section.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
              sx={{
                borderRadius: "50px",
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
                backgroundColor: "#9048FB",
                fontFamily: "var(--font-outfit)",
                color: "#FFFFFF",
                boxShadow: "none",
                transition: "transform 0.2s ease-in-out",
                mb: "2%",
                "&:hover": {
                  backgroundColor: "#000",
                  transform: "scale(1.05) translateY(-2px)",
                },
              }}
            >
              {t("heroSectionZayn.cta")}
            </Button>
          </Box>

          {/* Video Section */}
          <Box
            ref={videoSectionRef}
            sx={{
              position: "relative",
              maxWidth: "1000px",
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 5,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: `${videoWidth}px`,
                height: `${videoHeight}px`,
                maxWidth: "100%",
                transition: "all 0.3s ease-out",
                aspectRatio: scrollProgress === 0 ? "1 / 1" : "auto",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  borderRadius: borderRadiusValue,
                  overflow: "hidden",
                  transition: "all 0.3s ease-out",
                  "@media (min-width:1100px) and (max-width:1300px)": {
                    height: `${videoHeight * 0.85}px`,
                  },
                }}
              >
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                >
                  <source src={IMAGES.ZAYN_LOOP_VIDEO} type="video/mp4" />
                </video>

                {isClient && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Play Button */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2,
                    opacity: 1 - scrollProgress * 0.5,
                    transition: "opacity 0.3s ease-out",
                  }}
                >
                  <Box
                    onClick={() => {
                      setIsVideoModalOpen(true);
                      setIsPlaying(true);
                      setProgress(0);
                      setCurrentTime(0);
                    }}
                    sx={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.95)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                  >
                    <PlayArrowIcon
                      sx={{ fontSize: 40, color: "#7c3aed", ml: 0.5 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Video Modal */}
      <Modal
        open={isVideoModalOpen}
        onClose={() => {
          setIsVideoModalOpen(false);
          setIsPlaying(false);
          setProgress(0);
          setCurrentTime(0);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "80vh",
            maxWidth: "1200px",
            maxHeight: "800px",
            backgroundColor: "#000",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
            outline: "none",
          }}
        >
          <IconButton
            onClick={() => {
              setIsVideoModalOpen(false);
              setIsPlaying(false);
              setProgress(0);
              setCurrentTime(0);
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
              }
            }}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 3,
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <video
            ref={videoRef}
            autoPlay
            onEnded={handleVideoEnd}
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => setIsPlaying(true)}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          >
            <source src={IMAGES.ZAYN_FULL_VIDEO} type="video/mp4" />
          </video>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "16px",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
              zIndex: 3,
            }}
          >
            <Box
              ref={progressBarRef}
              onMouseDown={handleMouseDown}
              sx={{
                width: "100%",
                height: "6px",
                backgroundColor: "rgba(255,255,255,0.3)",
                borderRadius: "3px",
                cursor: "pointer",
                mb: 2,
                position: "relative",
                "&:hover": { height: "8px" },
                "&:hover .progress-thumb": {
                  opacity: 1,
                  transform: "translateY(-50%) scale(1)",
                },
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor: "#9048FB",
                  borderRadius: "3px",
                  transition: isDragging ? "none" : "width 0.1s linear",
                }}
              />
              <Box
                className="progress-thumb"
                sx={{
                  position: "absolute",
                  left: `calc(${progress}% - 8px)`,
                  top: "50%",
                  transform: "translateY(-50%) scale(0.8)",
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  cursor: "grab",
                  opacity: isDragging ? 1 : 0,
                  transition: "opacity 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  "&:active": {
                    cursor: "grabbing",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <IconButton
                onClick={togglePlayPause}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.7)",
                  color: "white",
                  width: 40,
                  height: 40,
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
                }}
              >
                {isPlaying ? (
                  <Box
                    sx={{ width: 16, height: 16, display: "flex", gap: "4px" }}
                  >
                    <Box sx={{ width: 4, height: 16, bgcolor: "white" }} />
                    <Box sx={{ width: 4, height: 16, bgcolor: "white" }} />
                  </Box>
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "14px",
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
});

HeroSectionZayn.displayName = "HeroSectionZayn";

export default HeroSectionZayn;
